<template>
  <AForm layout="vertical" labelAlign="left">
    <div style="overflow-x: hidden; overflow-y: scroll; height: 490px">
      <ARow :gutter="12">
        <ACol :span="12">
          <AFormItem label="Target Account" v-bind="validateInfos['account']">
            <ASelect
              v-model:value="state.account"
              placeholder="Target Account"
              allow-clear
            >
              <ASelect-option
                v-for="account of accounts"
                :value="account"
                :key="account._id"
              >
                {{ account.accountName }}
              </ASelect-option>
            </ASelect>
          </AFormItem>
        </ACol>

        <ACol :span="12">
          <div class="autopay">
            <span>Auto Pay</span>
            <ASwitch
              v-model:checked="state.autopay"
              checked-children="ON"
              un-checked-children="OFF"
              style="margin-left: 0.5rem;pointer-events: none;"
            />
          </div>
        </ACol>
      </ARow>
      <ATabs v-model:active-key="state.type" class="custom-ant-tabs">
        <ATabPane key="credit_card" :disabled="!hasPrimaPayIntegration">
          <template #tab>
            <span>
              <CreditCardOutlined />
              <span class="tap-card-full">Credit/Debit Card</span>
              <span class="tap-card">Card</span>
            </span>
          </template>
          <!-- Card form -->
          <CardForm
            v-if="!!accounts && accounts.length && state.account !== null"
            ref="refCardForm"
            :contactId="form.contact.id"
            :primaPayAccountId="state.account.lexChargeAccountId"
          />
          <!-- Select bank account for get hostedPaymentToken and init fields of LexCharge -->
          <h3 v-else-if="!!accounts && accounts.length">
            <img src="../assets/images/primaPay/primaPay.png" width="300" />
            <br />
            Select a bank account synced with PrimaPay integration.
          </h3>
          <!-- Not sync accounts with lex charge -->
          <h3 v-else>
            <img src="../assets/images/primaPay/primaPay.png" width="300" />
            <br />
            Sync bank accounts with PrimaPay integration.
          </h3>
        </ATabPane>
        <ATabPane key="e_check" :disabled="!hasPrimaPayIntegration">
          <template #tab>
            <span>
              <FileProtectOutlined />
              <span class="tap-card-full">E-Check/Bank</span>
              <span class="tap-card">E-Check</span>
            </span>
          </template>
          <!-- e-check form -->
          <ECheckForm
            v-if="!!accounts && accounts.length && state.account !== null"
            ref="refECheckForm"
            :contactId="form.contact.id"
            :primaPayAccountId="state.account.lexChargeAccountId"
          />
          <!-- Select bank account for get hostedPaymentToken and init fields of LexCharge -->
          <h3 v-else-if="!!accounts && accounts.length">
            <img src="../assets/images/primaPay/primaPay.png" width="300" />
            <br />
            Select a bank account synced with PrimaPay integration.
          </h3>
          <!-- Not sync accounts with lex charge -->
          <h3 v-else>
            <img src="../assets/images/primaPay/primaPay.png" width="300" />
            <br />
            Sync bank accounts with PrimaPay integration.
          </h3>
        </ATabPane>
        <ATabPane key="cash">
          <template #tab>
            <span>
              <DollarCircleOutlined />
              Cash
            </span>
          </template>
          <img src="../assets/images/primalaw/money.svg" alt="" width="197" />
          <h3>Prima.Law will save the information for this payment plan.</h3>
          <h3>This information will be not sent to PrimaPay.</h3>
          <h3>
            If you would like to process payments electronically, please obtain
            a PrimaPay account and connect it to Prima.Law
          </h3>
        </ATabPane>
      </ATabs>
    </div>
    <slot :validateFn="validateForm" />
  </AForm>
</template>

<script lang="ts">
import { defineComponent, reactive, Ref, ref, toRaw, watchEffect } from 'vue';
import {
  CreditCardOutlined,
  DollarCircleOutlined,
  FileProtectOutlined,
} from '@ant-design/icons-vue';
import CardForm from '@/components/CardForm.vue';
import ECheckForm from '@/components/ECheckForm.vue';
import { useForm } from '@ant-design-vue/use';
import { PaymentFormType } from '@/types/payment-form.type';
import { useIntegrations, useGlobalProps } from '@/composables';
import { IntegrationsService } from '@/services';
import { Validator } from '@/utils/ant-custom-validators';

interface State {
  type: PaymentFormType;
  autopay: boolean;
  account: string | null;
}

export default defineComponent({
  components: {
    CardForm,
    ECheckForm,
    CreditCardOutlined,
    DollarCircleOutlined,
    FileProtectOutlined,
  },
  setup() {
    const { setPaymentMethodSelected, form } = useGlobalProps();
    const refCardForm = ref<typeof CardForm>() as Ref<typeof CardForm>;
    const refECheckForm = ref<typeof ECheckForm>() as Ref<typeof ECheckForm>;

    const { accounts, setAccounts, hasPrimaPayIntegration } = useIntegrations();

    const state = reactive<State>({
      type: 'credit_card',
      autopay: false,
      account: null,
    });

    const rules = reactive({
      account: [
        {
          validator: Validator.isRequired(),
          trigger: 'change',
        },
      ],
    });

    const primaAccounts = ref<any[]>([]);

    /** Form validation result (AntDesign) */
    const { validateInfos, validate } = useForm(state, rules);

    /** ----- Methods ------ */
    const filterOption = (input: string, option: any) => {
      return option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    /** Validate form and return data */
    const validateForm = async () => {
      if (state.type === 'credit_card' || state.type === 'e_check') {
        // Credit card mode (primapay)
        try {
          // const primaPayRef = refCardForm;
          const primaPayRef =
            state.type === 'credit_card' ? refCardForm : refECheckForm;

          if (!primaPayRef.value.savedPaymentMethod.id) {
            const [, primaPayIsDone] = await Promise.all([
              validate('account'),
              primaPayRef.value.validate(),
            ]);

            if (!primaPayIsDone) {
              throw 'Invalid form fields';
            }
          } else {
            const accountIsDone = await Promise.all([validate('account')]);

            if (!accountIsDone) {
              throw 'Invalid account field';
            }
          }

          // Validation successfully
          const primaPay = await primaPayRef.value.primaPayRequest();
          return Promise.resolve({ ...toRaw(state), primaPay });
        } catch (error) {
          return Promise.reject(error);
        }
      } else {
        // Cash mode
        let done = true;

        // Validate other fields
        try {
          await validate();
        } catch (error) {
          done = false;
        }

        if (!done) {
          return Promise.reject(new Error('Invalid form values'));
        }

        return Promise.resolve(toRaw(state));
      }
    };

    const setStateType = () => {
      if (!hasPrimaPayIntegration.value) {
        state.type = 'cash';
        return;
      }
    };

    /** Execute request on create component */
    const executeRequests = async () => {
      if (accounts.value.length) return;

      // Get accounts
      try {
        primaAccounts.value = await IntegrationsService.getAccounts();
        setAccounts(primaAccounts.value);
        setStateType();
      } catch (error) {
        console.error(error);
      }
    };

    executeRequests();

    const alternAccounts = () => {
      state.account = null;

      if (state.type === 'credit_card' || state.type === 'e_check') {
        setAccounts(
          primaAccounts.value.filter(
            account =>
              account.lexChargeAccountId && account.lexChargeAccountId !== null,
          ),
        );

        state.autopay = true;
      } else {
        setAccounts(primaAccounts.value);
        state.autopay = false;
      }

      return;
    };

    watchEffect(() => {
      alternAccounts();

      setPaymentMethodSelected(state.type);
    });

    return {
      state,
      accounts,
      validateInfos,
      filterOption,
      validateForm,
      refCardForm,
      alternAccounts,
      primaAccounts,
      setStateType,
      hasPrimaPayIntegration,
      refECheckForm,
      form,
    };
  },
});
</script>

<style lang="scss">
.Contact {
  display: flex;
}

.card-brand {
  position: absolute;
  right: 8px;
  height: 24px;
  top: 3px;
  pointer-events: none;
}

.autopay {
  display: flex;
  justify-content: flex-end;
  height: 94px;
  align-items: center;
}

.tap-card {
  @include screen-sm {
    display: none;
  }

  &-full {
    display: none;

    @include screen-sm {
      display: inline;
    }
  }
}

.custom-ant-tabs {
  .ant-tabs-nav {
    width: 100%;
  }

  .ant-tabs-tab {
    width: 33.3%;
    margin: 0;
  }

  .ant-tabs-ink-bar {
    width: 33.3% !important;
  }
}
</style>
